import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, ButtonGroup, Modal, Form, Table, Dropdown } from 'react-bootstrap';
import moment from 'moment';
import dayjs from 'dayjs';
import {jwtDecode} from "jwt-decode";
import axiosInstance from '../../api/axiosConfig';
import 'moment/locale/nl';

// Nederlandse vertalingen voor dagen en maanden
const dutchDayNames = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
const dutchDayNamesShort = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const dutchMonths = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
const dutchMonthsShort = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

// Set Dutch locale for moment
moment.locale('nl');
// Set Dutch locale for dayjs
const localeNL = {
    name: 'nl',
    weekdays: dutchDayNames,
    months: dutchMonths,
    weekStart: 1,
};
dayjs.locale(localeNL);

const Scheduler = () => {
    const [hasEditPermission, setHasEditPermission] = useState(false);
    const [currentWeekStart, setCurrentWeekStart] = useState(moment().startOf('week'));
    const [currentDay, setCurrentDay] = useState(moment().format('YYYY-MM-DD')); // State for current day
    const [viewMode, setViewMode] = useState('week'); // 'week' or 'day'
    const [showModal, setShowModal] = useState(false);
    const [showModalEdit, setshowModalEdit] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [schedule, setSchedule] = useState({});
    const [companyId, setCompanyId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null); // State voor de geselecteerde categorie
    const [currentEvent, setCurrentEvent] = useState({
        employee: "",
        startDay: moment().format("YYYY-MM-DD"),
        startTime: "09:00",
        endDay: moment().format("YYYY-MM-DD"),
        endTime: "17:00",
        isOvernightShift: false
    });

    const CATEGORIES = {
        'Restaurant': ['Gastheer', 'Gastvrouw', 'Ober', 'Bedieningsmedewerker', 'Barkeeper', 'Sommelier', 'Barista', 'Receptionist', 'Runner', 'Loper'],
        'Keuken': ['Chef-kok', 'Souschef', 'Chef de Partie', 'Saucier', 'Garde Manger', 'Patisserie', 'Keukenhulp', 'Afwasser', 'Sushichef', 'Pizzabakker', 'Grillmaster'],
        'Club': ['DJ', 'Beveiligingsmedewerker', 'Light Jockey', 'Sound Engineer', 'Promotiemedewerker', 'VIP Host', 'Bedieningsmedewerker', 'Barkeeper', 'Gastheer', 'Gastvrouw', 'Eventmanager', 'Club-manager'],
        'Management en Supervisie': ['Baas', 'Bedrijfsleider', 'Floormanager', 'Shiftleider', 'Keukenmanager', 'Barmanager'],
        'Ondersteunende Diensten': ['Inkoper', 'Voorraadbeheerder', 'Schoonmaker', 'Housekeeping'],
        'Evenementen en Catering': ['Eventmanager', 'Cateringmanager', 'Banquetingmedewerker'],
        'Overige': ['Conciërge', 'Roomservice medewerker', 'Portier'],
    };

    useEffect(() => {
        getUserInfoFromToken();
    }, []);

    const getUserInfoFromToken = () => {
        try {
            const authToken = sessionStorage.getItem('authToken');
            if (authToken) {
                const tokenData = jwtDecode(authToken);
                const userCompanyId = tokenData.companyId;
                setCompanyId(userCompanyId);

                const requiredRoles = ['BAAS', 'HUMANRESOURCE', 'HORECAMASTERS', 'FLOORMANAGER', 'CHEFKOK'];
                const userRoles = tokenData.roles || [];
                const hasPermission = requiredRoles.some(role => userRoles.includes(role));
                setHasEditPermission(hasPermission);
            } else {
                setHasEditPermission(false);
                setCompanyId(null);
            }
        } catch (error) {
            console.error("Error checking user permissions:", error);
            setHasEditPermission(false);
            setCompanyId(null);
        }
    };

    useEffect(() => {
        if (!companyId) return;

        const fetchData = async () => {
            try {
                setIsLoading(true);

                const [employeesRes, schedulesRes] = await Promise.all([
                    axiosInstance.get(`/employees/company/${companyId}`),
                    axiosInstance.get(`/schedules/company/${companyId}`)
                ]);

                console.log("Volledige werknemers data:", employeesRes.data);
                console.log("Volledige roosters data:", schedulesRes.data);

                setEmployees(employeesRes.data.map(emp => ({
                    ...emp,
                    name: `${emp.firstName} ${emp.lastName}`
                })));

                const scheduleMap = {};

                schedulesRes.data.forEach(shift => {
                    const email = shift.employee.email;
                    if (!scheduleMap[email]) {
                        scheduleMap[email] = [];
                    }

                    scheduleMap[email].push({
                        ...shift,
                        startTime: dayjs(shift.startTime),
                        endTime: dayjs(shift.endTime),
                        shiftId: shift.scheduleId, // ✅ Altijd de originele shiftId gebruiken
                        isTemporary: false
                    });
                });

                // Tijdelijke shifts uit sessionStorage toevoegen
                const tempShifts = JSON.parse(sessionStorage.getItem('temporaryShifts') || '[]');
                tempShifts.forEach(shift => {
                    const email = shift.employeeEmail;
                    if (!scheduleMap[email]) {
                        scheduleMap[email] = [];
                    }

                    scheduleMap[email].push({
                        ...shift,
                        startTime: dayjs(shift.startTime),
                        endTime: dayjs(shift.endTime),
                        shiftId: `temp-${new Date(shift.startTime).getTime()}`, // ✅ Alleen temp- ID voor tijdelijke shifts
                        isTemporary: true
                    });
                });

                console.log("Opgebouwde scheduleMap:", scheduleMap);
                setSchedule(scheduleMap);
            } catch (error) {
                console.error('Fout bij het ophalen van data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [companyId]);

    // Verbeterde functie met handmatige Nederlandse dag- en maandnamen
    const getDaysOfWeek = () => {
        const days = [];
        const startOfWeek = moment(currentWeekStart);

        for (let i = 0; i < 7; i++) {
            const day = moment(startOfWeek).add(i, 'days');
            // Bereken index voor dutchDayNamesShort (0-6 naar 0-6)
            const dayIndex = day.day() === 0 ? 6 : day.day() - 1; // Converteer zondag=0 naar zondag=6
            const dutchDay = dutchDayNamesShort[dayIndex];

            // Verkrijg maandindex (0-11) voor de Nederlandse maandafkorting
            const monthIndex = day.month();
            const dutchMonth = dutchMonthsShort[monthIndex];

            days.push({
                date: day.format('YYYY-MM-DD'),
                display: `${dutchDay} ${day.format('D')} ${dutchMonth}`
            });
        }
        return days;
    };

    // Verbeterde functie voor de enkele dag met handmatige Nederlandse dagnamen
    const getSingleDay = () => {
        const day = moment(currentDay);
        // Bereken index voor dutchDayNames (0-6 naar 0-6)
        const dayIndex = day.day() === 0 ? 6 : day.day() - 1; // Converteer zondag=0 naar zondag=6
        const dutchDay = dutchDayNames[dayIndex];

        // Verkrijg maandindex (0-11) voor de Nederlandse maandnaam
        const monthIndex = day.month();
        const dutchMonth = dutchMonths[monthIndex];

        return {
            date: day.format('YYYY-MM-DD'),
            display: `${dutchDay} ${day.format('D')} ${dutchMonth} ${day.format('YYYY')}`
        };
    };

    const getShiftsForDay = (employee, date) => {
        if (!schedule[employee.email]) return [];

        return schedule[employee.email].filter(shift => {
            const shiftDate = shift.startTime.format('YYYY-MM-DD');
            return shiftDate === date;
        });
    };

    const formatShiftTime = (shift) => {
        return `${shift.startTime.format('HH:mm')} - ${shift.endTime.format('HH:mm')}`;
    };

    const goToPreviousWeek = () => {
        setCurrentWeekStart(moment(currentWeekStart).subtract(1, 'week'));
    };

    const goToNextWeek = () => {
        setCurrentWeekStart(moment(currentWeekStart).add(1, 'week'));
    };

    const goToCurrentWeek = () => {
        setCurrentWeekStart(moment().startOf('week'));
    };

    // Day navigation functions
    const goToPreviousDay = () => {
        setCurrentDay(moment(currentDay).subtract(1, 'day').format('YYYY-MM-DD'));
    };

    const goToNextDay = () => {
        setCurrentDay(moment(currentDay).add(1, 'day').format('YYYY-MM-DD'));
    };

    const goToCurrentDay = () => {
        setCurrentDay(moment().format('YYYY-MM-DD'));
    };

    // Toggle between week and day view
    const toggleViewMode = () => {
        if (viewMode === 'week') {
            setViewMode('day');
            // Set current day to the first day of the current week if needed
            // or keep it as is if it's already set
            if (!currentDay) {
                setCurrentDay(moment(currentWeekStart).format('YYYY-MM-DD'));
            }
        } else {
            setViewMode('week');
            // Make sure current week includes the current day
            const dayMoment = moment(currentDay);
            if (!dayMoment.isBetween(moment(currentWeekStart), moment(currentWeekStart).add(6, 'days'), null, '[]')) {
                setCurrentWeekStart(moment(currentDay).startOf('week'));
            }
        }
    };

    const handleClose = () => setShowModal(false);
    const handleShow = () => setShowModal(true);

    const handleCloseEdit = () => setshowModalEdit(false);
    const handleShowEdit = () => setshowModalEdit(true);

    const saveShift = () => {
        const selectedEmployee = employees.find(emp => emp.name === currentEvent.employee);
        if (!selectedEmployee) return;

        const startDateTime = `${currentEvent.startDay}T${currentEvent.startTime}:00`;
        const endDateTime = `${currentEvent.endDay}T${currentEvent.endTime}:00`;

        const newShift = {
            employeeEmail: selectedEmployee.email,
            companyId: companyId,
            startTime: startDateTime,
            endTime: endDateTime
        };

        const existingShifts = JSON.parse(sessionStorage.getItem('temporaryShifts') || '[]');
        const updatedShifts = [...existingShifts, newShift];
        sessionStorage.setItem('temporaryShifts', JSON.stringify(updatedShifts));

        const updatedSchedule = { ...schedule };
        if (!updatedSchedule[selectedEmployee.email]) {
            updatedSchedule[selectedEmployee.email] = [];
        }
        updatedSchedule[selectedEmployee.email].push({
            startTime: dayjs(startDateTime),
            endTime: dayjs(endDateTime),
            shiftId: `temp-${Date.now()}`,
            isTemporary: true
        });

        setSchedule(updatedSchedule);
        handleClose();
    };

    const handleAddEventFromSession = async () => {
        const storedShifts = JSON.parse(sessionStorage.getItem('temporaryShifts')) || [];

        if (storedShifts.length === 0) {
            alert("Geen diensten gevonden in sessionStorage om naar de server te sturen.");
            return;
        }

        setIsSaving(true);

        try {
            for (const shift of storedShifts) {
                const response = await axiosInstance.post('/schedules/create', shift);

                if (response.status === 200 || response.status === 201) {
                    console.log("Dienst toegevoegd:", shift);
                } else {
                    console.error("Fout bij het toevoegen van dienst:", shift);
                    alert("Er is een fout opgetreden bij het toevoegen van de dienst.");
                }
            }

            sessionStorage.removeItem('temporaryShifts');

            const schedulesRes = await axiosInstance.get(`/schedules/company/${companyId}`);
            const scheduleMap = {};
            schedulesRes.data.forEach(shift => {
                const email = shift.employee.email;
                if (!scheduleMap[email]) {
                    scheduleMap[email] = [];
                }
                scheduleMap[email].push({
                    startTime: dayjs(shift.startTime),
                    endTime: dayjs(shift.endTime),
                    shiftId: shift.id,
                    isTemporary: false
                });
            });

            setSchedule(scheduleMap);

            setCurrentEvent({
                employee: "",
                startDay: moment().format("YYYY-MM-DD"),
                startTime: "09:00",
                endDay: moment().format("YYYY-MM-DD"),
                endTime: "17:00",
                isOvernightShift: false
            });

            handleClose();
            alert("Alle diensten zijn succesvol toegevoegd aan het rooster.");

        } catch (error) {
            console.error("Fout bij het toevoegen van dienst:", error);
            alert("Er is een fout opgetreden bij het toevoegen van de dienst");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteShift = async () => {
        if (!currentEvent || !currentEvent.shiftId) {
            console.error("🚨 FOUT: Geen shiftId gevonden bij verwijderen!");
            return;
        }

        setIsDeleting(true);

        try {
            if (currentEvent.shiftId.startsWith('temp-')) {
                console.log("Verwijderen van tijdelijke dienst...");

                const storedShifts = JSON.parse(sessionStorage.getItem('temporaryShifts') || '[]');
                const updatedShifts = storedShifts.filter(shift =>
                    `temp-${new Date(shift.startTime).getTime()}` !== currentEvent.shiftId
                );
                sessionStorage.setItem('temporaryShifts', JSON.stringify(updatedShifts));

            } else {
                console.log("Verwijderen van permanente dienst via API...");

                // Controleer de daadwerkelijke URL
                const deleteUrl = `/schedules/delete/${currentEvent.shiftId}`;
                console.log("DELETE verzoek naar:", deleteUrl);

                // Verzend de DELETE-verzoek naar de API
                const response = await axiosInstance.delete(deleteUrl);

                console.log("API-reactie ontvangen:", response);

                if (response.status !== 200 && response.status !== 204) {
                    throw new Error(`Verwijderen mislukt met status: ${response.status}`);
                }
            }

            // Update de lokale schedule state
            console.log("Bijwerken van de lokale schedule...");
            const updatedSchedule = { ...schedule };
            const selectedEmployee = employees.find(emp => emp.name === currentEvent.employee);
            if (selectedEmployee && updatedSchedule[selectedEmployee.email]) {
                updatedSchedule[selectedEmployee.email] = updatedSchedule[selectedEmployee.email]
                    .filter(shift => shift.shiftId !== currentEvent.shiftId);
            }
            setSchedule(updatedSchedule);

            handleClose();
            alert("Dienst is succesvol verwijderd.");
        } catch (error) {
            console.error("Fout bij het verwijderen van de dienst:", error);

            if (error.response) {
                console.error("Foutdetails:", error.response.status, error.response.data);
            }

            alert("Er is een fout opgetreden bij het verwijderen van de dienst. Probeer het opnieuw.");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleShiftClick = (employee, shift) => {
        // Zorg ervoor dat shiftId een string is
        const shiftId = shift.shiftId ? String(shift.shiftId) : undefined;

        if (!shiftId) {
            console.error("⚠️ Geen geldige shiftId gevonden!", shift);
        } else {
            console.log("✅ Geldige shiftId gevonden:", shiftId);
        }

        const employeeData = shift.employee || employee;

        const eventDetails = {
            employee: `${employeeData.firstName} ${employeeData.lastName}`,
            employeeId: employeeData.employeeId,
            email: employeeData.email,
            startDay: shift.startTime.format('YYYY-MM-DD'),
            startTime: shift.startTime.format('HH:mm'),
            endDay: shift.endTime.format('YYYY-MM-DD'),
            endTime: shift.endTime.format('HH:mm'),
            shiftId: shiftId, // Nu altijd een string
            isTemporary: shift.isTemporary
        };

        console.log("🎯 Dienst geselecteerd:", eventDetails);

        setCurrentEvent(eventDetails);
        handleShowEdit(true);
    };

    // Functie voor het wijzigen van de categorie via dropdown
    const handleCategoryChange = (category) => {
        setSelectedCategory(category === "all" ? null : category);
    };

    // Functie voor gefilterde werknemers op basis van geselecteerde categorie
    const filteredEmployees = employees.filter((employee) => {
        if (!selectedCategory) return true;
        return CATEGORIES[selectedCategory]?.includes(employee.jobTitle);
    });

    const weekDays = getDaysOfWeek();
    const singleDay = getSingleDay();

    const styles = {
        schedulerWrapper: {
            overflowX: 'auto',
            maxWidth: '100vw'
        },
        schedulerTable: {
            minWidth: '768px',
            tableLayout: 'fixed'
        },
        employeeCell: {
            width: '150px',
            minWidth: '150px',
            backgroundColor: '#f8f9fa',
            position: 'sticky',
            left: 0,
            zIndex: 1
        },
        dayCell: {
            width: 'calc((100% - 150px) / 7)',
            minWidth: '100px',
            padding: '0.5rem',
            verticalAlign: 'top'
        },
        dayCellSingle: {
            width: 'calc(100% - 150px)',
            minWidth: '100px',
            padding: '0.5rem',
            verticalAlign: 'top'
        },
        shiftItem: {
            backgroundColor: '#007bff',
            color: 'white',
            padding: '0.25rem',
            borderRadius: '4px',
            marginBottom: '0.25rem',
            fontSize: '0.875rem',
            cursor: 'pointer'
        },
        temporaryShiftItem: {
            backgroundColor: '#ff8c00',
            color: 'white',
            padding: '0.25rem',
            borderRadius: '4px',
            marginBottom: '0.25rem',
            fontSize: '0.875rem',
            cursor: 'pointer'
        },
        categoryDropdown: {
            marginBottom: '15px'
        }
    };


    return (
        <Container fluid className="mt-3 mb-3" style={{width: '93vw'}}>
            <Row className="mb-3">
                <Col xs={12} className="d-flex flex-column gap-2">
                    <div className="d-flex gap-2">
                        {viewMode === 'week' ? (
                            <ButtonGroup>
                                <Button variant="outline-secondary" onClick={goToPreviousWeek}>
                                    &laquo;
                                </Button>
                                <Button variant="outline-primary" onClick={goToCurrentWeek}>
                                    Deze Week
                                </Button>
                                <Button variant="outline-secondary" onClick={goToNextWeek}>
                                    &raquo;
                                </Button>
                            </ButtonGroup>
                        ) : (
                            <ButtonGroup>
                                <Button variant="outline-secondary" onClick={goToPreviousDay}>
                                    &laquo;
                                </Button>
                                <Button variant="outline-primary" onClick={goToCurrentDay}>
                                    Vandaag
                                </Button>
                                <Button variant="outline-secondary" onClick={goToNextDay}>
                                    &raquo;
                                </Button>
                            </ButtonGroup>
                        )}

                        <Button
                            variant="outline-primary"
                            onClick={toggleViewMode}
                        >
                            {viewMode === 'week' ? "Dagweergave" : "Weekweergave" }
                        </Button>
                    </div>

                    <div className="d-flex gap-2">
                        {hasEditPermission && (
                            <Button variant="primary" onClick={handleShow}>
                                Nieuwe Dienst
                            </Button>
                        )}

                        {hasEditPermission && (
                            <Button variant="success" onClick={handleAddEventFromSession}>
                                Rooster Bevestigen
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Categorie dropdown menu */}
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Group style={styles.categoryDropdown}>
                        <Form.Label>Filter op afdeling:</Form.Label>
                        <Form.Select
                            value={selectedCategory || "all"}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                        >
                            <option value="all">Alle afdelingen</option>
                            {Object.keys(CATEGORIES).map(category => (
                                <option key={category} value={category}>
                                    {category}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {hasEditPermission && (
                <div className="mb-4">
                    <div className="d-flex align-items-center">
                        <div className="me-3 d-flex align-items-center">
                            <div style={{width: "20px", height: "20px", backgroundColor: "#007bff", marginRight: "5px"}}></div>
                            <span>Definitief Rooster</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <div style={{width: "20px", height: "20px", backgroundColor: "#ff8c00", marginRight: "5px"}}></div>
                            <span>Concept Rooster</span>
                        </div>
                    </div>
                </div>
            )}

            <div style={styles.schedulerWrapper}>
                {viewMode === 'week' ? (
                    // Week view (original table)
                    <Table bordered style={styles.schedulerTable}>
                        <thead>
                        <tr>
                            <th style={styles.employeeCell}>Medewerkers</th>
                            {weekDays.map(day => (
                                <th key={day.date} style={styles.dayCell}>
                                    {day.display}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {filteredEmployees.map(employee => (
                            <tr key={employee.employeeId}>
                                <td style={styles.employeeCell}>
                                    <div>
                                        <div>{employee.name}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>{employee.jobTitle}</div>
                                    </div>
                                </td>
                                {weekDays.map(day => (
                                    <td key={`${employee.employeeId}-${day.date}`} style={styles.dayCell}>
                                        {getShiftsForDay(employee, day.date).map((shift, index) => (
                                            <div
                                                key={`${shift.shiftId}-${index}`}
                                                style={shift.isTemporary ? styles.temporaryShiftItem : styles.shiftItem}
                                                onClick={() => handleShiftClick(employee, shift)}
                                            >
                                                {formatShiftTime(shift)}
                                            </div>
                                        ))}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                ) : (
                    // Day view with time blocks
                    // This is the fixed part of your code for the day view that properly handles overnight shifts
// Look for the changes in the code for handling shifts that cross midnight

// When in day view, for the time blocks table
                    <div style={{maxWidth: '70%', overflow: 'auto'}}>
                        <h4>{singleDay.display}</h4>
                        <Table bordered style={{...styles.schedulerTable, tableLayout: 'fixed', width: '100%'}}>
                            <thead>
                            <tr>
                                <th style={{...styles.employeeCell, width: '100px', minWidth: '100px'}}>Medewerkers</th>
                                {Array.from({length: 24}).map((_, hour) => (
                                    <th key={hour}
                                        style={{width: '30px', minWidth: '30px', textAlign: 'center', padding: '2px'}}>
                                        {hour.toString().padStart(2, '0')}
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {filteredEmployees.map(employee => {
                                // Get shifts for the current day and the previous day (for overnight shifts)
                                const shiftsForCurrentDay = getShiftsForDay(employee, singleDay.date);

                                // Get previous day's date
                                const previousDay = moment(singleDay.date).subtract(1, 'day').format('YYYY-MM-DD');
                                const shiftsForPreviousDay = getShiftsForDay(employee, previousDay);

                                // Filter previous day shifts that continue into current day (end time > start time)
                                const overnightShifts = shiftsForPreviousDay.filter(shift => {
                                    const shiftStartHour = parseInt(shift.startTime.format('HH'));
                                    const shiftEndHour = parseInt(shift.endTime.format('HH'));
                                    return shiftStartHour > shiftEndHour; // This indicates an overnight shift
                                });

                                return (
                                    <tr key={employee.employeeId}>
                                        <td style={{...styles.employeeCell, width: '100px', minWidth: '100px'}}>
                                            <div>
                                                <div style={{fontSize: '0.85rem'}}>{employee.name}</div>
                                                <div style={{
                                                    fontSize: '0.7rem',
                                                    color: '#6c757d'
                                                }}>{employee.jobTitle}</div>
                                            </div>
                                        </td>
                                        {Array.from({length: 24}).map((_, hour) => {
                                            // Check if this hour is covered by any current day shift
                                            const hasCurrentDayShift = shiftsForCurrentDay.some(shift => {
                                                const shiftStartHour = parseInt(shift.startTime.format('HH'));
                                                const shiftEndHour = parseInt(shift.endTime.format('HH'));

                                                // For overnight shifts (within current day)
                                                if (shiftStartHour > shiftEndHour) {
                                                    return hour >= shiftStartHour; // Only show first part on current day
                                                }

                                                return hour >= shiftStartHour && hour < shiftEndHour;
                                            });

                                            // Check if this hour is covered by an overnight shift from previous day
                                            const hasOvernightShift = overnightShifts.some(shift => {
                                                const shiftEndHour = parseInt(shift.endTime.format('HH'));
                                                return hour < shiftEndHour; // Show second part on next day
                                            });

                                            // Determine if this hour has any shift
                                            const hasShift = hasCurrentDayShift || hasOvernightShift;

                                            // Find the relevant shift for this hour
                                            let relevantShift = shiftsForCurrentDay.find(shift => {
                                                const shiftStartHour = parseInt(shift.startTime.format('HH'));
                                                const shiftEndHour = parseInt(shift.endTime.format('HH'));

                                                if (shiftStartHour > shiftEndHour) {
                                                    return hour >= shiftStartHour;
                                                }

                                                return hour >= shiftStartHour && hour < shiftEndHour;
                                            });

                                            // If no current day shift but has overnight shift, use that one
                                            if (!relevantShift && hasOvernightShift) {
                                                relevantShift = overnightShifts.find(shift => {
                                                    const shiftEndHour = parseInt(shift.endTime.format('HH'));
                                                    return hour < shiftEndHour;
                                                });
                                            }

                                            // Check if this hour is the start of a shift (to show the time)
                                            const isShiftStart = shiftsForCurrentDay.some(shift =>
                                                parseInt(shift.startTime.format('HH')) === hour
                                            );

                                            // Check if this hour is the end of a shift (one hour before end time)
                                            const isShiftEnd = relevantShift &&
                                                ((parseInt(relevantShift.endTime.format('HH')) === hour + 1) ||
                                                    (hour === 23 && parseInt(relevantShift.endTime.format('HH')) === 0));

                                            // Check if this is the beginning of the day (hour 0) and there's an overnight shift
                                            const isOvernightContinuation = hour === 0 && hasOvernightShift;

                                            return (
                                                <td
                                                    key={`${employee.employeeId}-${hour}`}
                                                    style={{
                                                        backgroundColor: hasShift
                                                            ? (relevantShift?.isTemporary ? '#ff8c00' : '#007bff')
                                                            : 'transparent',
                                                        color: hasShift ? 'white' : 'inherit',
                                                        cursor: hasShift ? 'pointer' : 'default',
                                                        position: 'relative',
                                                        padding: '2px',
                                                        width: '30px',
                                                        minWidth: '30px'
                                                    }}
                                                    onClick={() => hasShift && handleShiftClick(employee, relevantShift)}
                                                >
                                                    {isShiftStart && relevantShift && (
                                                        <div style={{
                                                            fontSize: '0.6rem',
                                                            whiteSpace: 'nowrap',
                                                            position: 'absolute',
                                                            top: '1px',
                                                            left: '1px'
                                                        }}>
                                                            {relevantShift.startTime.format('HH:mm')}
                                                        </div>
                                                    )}
                                                    {isShiftEnd && relevantShift && (
                                                        <div style={{
                                                            fontSize: '0.6rem',
                                                            whiteSpace: 'nowrap',
                                                            position: 'absolute',
                                                            bottom: '1px',
                                                            right: '1px'
                                                        }}>
                                                            {relevantShift.endTime.format('HH:mm')}
                                                        </div>
                                                    )}
                                                    {isOvernightContinuation && (
                                                        <div style={{
                                                            fontSize: '0.6rem',
                                                            whiteSpace: 'nowrap',
                                                            position: 'absolute',
                                                            top: '1px',
                                                            left: '1px'
                                                        }}>
                                                            00:00
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>

            {/* Edit Shift Modal */}
            {hasEditPermission && (
                <Modal show={showModalEdit} onHide={handleCloseEdit}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            Dienst Details {currentEvent.isTemporary ? '(Concept)' : ''}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Medewerker</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={currentEvent.employee || ''}
                                    disabled
                                />
                            </Form.Group>

                            <Row>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Begindatum</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={currentEvent.startDay}
                                            disabled={!currentEvent.isEditable}
                                            onChange={(e) => setCurrentEvent({
                                                ...currentEvent,
                                                startDay: e.target.value
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Begintijd</Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={currentEvent.startTime}
                                            disabled={!currentEvent.isEditable}
                                            onChange={(e) => setCurrentEvent({
                                                ...currentEvent,
                                                startTime: e.target.value
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Einddatum</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={currentEvent.endDay}
                                            disabled={!currentEvent.isEditable}
                                            onChange={(e) => setCurrentEvent({...currentEvent, endDay: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Eindtijd</Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={currentEvent.endTime}
                                            disabled={!currentEvent.isEditable}
                                            onChange={(e) => setCurrentEvent({
                                                ...currentEvent,
                                                endTime: e.target.value
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="d-flex justify-content-between w-100">
                            <Button
                                variant="danger"
                                onClick={() => {
                                    if (window.confirm('Weet je zeker dat je deze dienst wilt verwijderen?')) {
                                        handleDeleteShift();
                                    }
                                }}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Bezig met verwijderen...' : 'Verwijderen'}
                            </Button>

                            <div>
                                <Button variant="secondary" onClick={handleCloseEdit}>
                                    Sluiten
                                </Button>
                            </div>
                        </div>
                    </Modal.Footer>
                </Modal>
            )}

            {/* New Shift Modal */}
            <Modal show={showModal} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Nieuwe Dienst</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Medewerker</Form.Label>
                            <Form.Select
                                value={currentEvent.employee || ''}
                                onChange={(e) => setCurrentEvent({...currentEvent, employee: e.target.value})}
                            >
                                <option value="">Selecteer een medewerker</option>
                                {employees.map((employee) => (
                                    <option
                                        key={employee.employeeId}
                                        value={`${employee.firstName} ${employee.lastName}`}
                                    >
                                        {`${employee.firstName} ${employee.lastName}`}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Begindatum</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={currentEvent.startDay}
                                        onChange={(e) => setCurrentEvent({...currentEvent, startDay: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Begintijd</Form.Label>
                                    <Form.Control
                                        type="time"
                                        value={currentEvent.startTime}
                                        onChange={(e) => setCurrentEvent({...currentEvent, startTime: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Einddatum</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={currentEvent.endDay}
                                        onChange={(e) => setCurrentEvent({...currentEvent, endDay: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Eindtijd</Form.Label>
                                    <Form.Control
                                        type="time"
                                        value={currentEvent.endTime}
                                        onChange={(e) => setCurrentEvent({...currentEvent, endTime: e.target.value})}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} className="me-2">
                        Annuleren
                    </Button>
                    <Button
                        variant="primary"
                        onClick={saveShift}
                        disabled={isDeleting}
                    >
                        Opslaan
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default Scheduler;
